// OfflineBanner.jsx — Offline-Status + Queue-Puffer Anzeige
import React, { useState, useEffect, useRef } from 'react';
import './OfflineBanner.css';
import { getPendingCount, replayAll } from '../utils/offlineQueue';

export default function OfflineBanner({ swRegistration }) {
  const [isOffline, setIsOffline]             = useState(!navigator.onLine);
  const [pendingCount, setPendingCount]       = useState(0);
  const [syncState, setSyncState]             = useState(null); // null | 'syncing' | 'done' | 'partial'
  const [syncDetail, setSyncDetail]           = useState(null); // { replayed, failed }
  const [showReconnected, setShowReconnected] = useState(false);
  const [showUpdate, setShowUpdate]           = useState(false);
  const [pendingReg, setPendingReg]           = useState(null);
  // Vom Server abgelehnte Einträge (4xx beim Nachsenden): bleiben sichtbar,
  // bis jemand sie wegklickt — sonst wäre die Wertung still verschwunden.
  const [verworfen, setVerworfen]             = useState([]);
  const wasOfflineRef = useRef(false);
  const syncTimerRef  = useRef(null);

  // ── Queue-Count aktuell halten ──────────────────────────────────────────────
  const refreshCount = async () => {
    try {
      const n = await getPendingCount();
      setPendingCount(n);
    } catch (_) {}
  };

  useEffect(() => {
    refreshCount();
    const handler = () => refreshCount();
    window.addEventListener('offline-queue-changed', handler);
    return () => window.removeEventListener('offline-queue-changed', handler);
  }, []);

  // ── Online/Offline Events ───────────────────────────────────────────────────
  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      wasOfflineRef.current = true;
    };
    const handleOnline = async () => {
      setIsOffline(false);
      const count = await getPendingCount();
      if (count > 0) {
        // Ausstehende Requests automatisch nachsenden
        setSyncState('syncing');
        try {
          const result = await replayAll();
          setSyncDetail(result);
          setSyncState(result.failed > 0 ? 'partial' : result.replayed > 0 ? 'done' : null);
        } catch (_) {
          setSyncState('partial');
          setSyncDetail({ replayed: 0, failed: count });
        }
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
          setSyncState(null);
          setSyncDetail(null);
        }, 4000);
        refreshCount();
      } else if (wasOfflineRef.current) {
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3500);
      }
      wasOfflineRef.current = false;
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online',  handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online',  handleOnline);
    };
  }, []);

  // ── Sync-Events (von offlineQueue.replayAll) ────────────────────────────────
  useEffect(() => {
    const onSyncing = (e) => {
      setSyncState('syncing');
      setSyncDetail({ total: e.detail?.total });
    };
    const onSynced = (e) => {
      setSyncDetail(e.detail);
      if (e.detail?.verworfen?.length) setVerworfen((v) => [...v, ...e.detail.verworfen]);
      setSyncState(e.detail?.failed > 0 ? 'partial' : e.detail?.replayed > 0 ? 'done' : null);
      refreshCount();
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        setSyncState(null);
        setSyncDetail(null);
      }, 4000);
    };
    window.addEventListener('offline-queue-syncing', onSyncing);
    window.addEventListener('offline-queue-synced',  onSynced);
    return () => {
      window.removeEventListener('offline-queue-syncing', onSyncing);
      window.removeEventListener('offline-queue-synced',  onSynced);
    };
  }, []);

  // ── Liegengebliebenes nachsenden ────────────────────────────────────────────
  // Beim Laden der Seite und danach alle 30 s, solange etwas wartet und der
  // Browser online meldet (5xx beim letzten Versuch, oder Seite neu geladen).
  useEffect(() => {
    const versuch = async () => {
      if (!navigator.onLine) return;
      try { if ((await getPendingCount()) > 0) await replayAll(); } catch (_) {}
    };
    versuch();
    const t = setInterval(versuch, 30000);
    return () => clearInterval(t);
  }, []);

  // ── Service-Worker Update-Event ─────────────────────────────────────────────
  useEffect(() => {
    if (!swRegistration) return;
    setPendingReg(swRegistration);
    setShowUpdate(true);
  }, [swRegistration]);

  const applyUpdate = () => {
    if (pendingReg?.waiting) pendingReg.waiting.postMessage({ type: 'SKIP_WAITING' });
    setShowUpdate(false);
  };

  const isVisible = isOffline || showReconnected || showUpdate || syncState !== null || pendingCount > 0 || verworfen.length > 0;
  if (!isVisible) return null;

  return (
    <div className="offline-banner-container">

      {/* Update-Banner */}
      {showUpdate && !isOffline && (
        <div className="offline-banner update-banner">
          <span>🔄 Neue Version verfügbar</span>
          <button className="offline-action-btn" onClick={applyUpdate}>
            Jetzt aktualisieren
          </button>
          <button className="offline-close-btn" onClick={() => setShowUpdate(false)}>✕</button>
        </div>
      )}

      {/* Sync läuft gerade */}
      {syncState === 'syncing' && (
        <div className="offline-banner syncing-banner">
          <span className="offline-sync-spinner" />
          <span>
            Synchronisiere{syncDetail?.total ? ` ${syncDetail.total}` : ''} ausstehende Wertung{syncDetail?.total !== 1 ? 'en' : ''}…
          </span>
        </div>
      )}

      {/* Sync abgeschlossen */}
      {syncState === 'done' && syncDetail && (
        <div className="offline-banner synced-banner">
          <span>✅ {syncDetail.replayed} Wertung{syncDetail.replayed !== 1 ? 'en' : ''} synchronisiert</span>
        </div>
      )}

      {/* Sync teilweise fehlgeschlagen */}
      {syncState === 'partial' && syncDetail && (
        <div className="offline-banner sync-partial-banner">
          <span>⚠️ Nachsenden: {syncDetail.replayed} gespeichert, {syncDetail.failed} warten weiter</span>
        </div>
      )}

      {/* Vom Server abgelehnt: NICHT gespeichert */}
      {verworfen.length > 0 && (
        <div className="offline-banner sync-partial-banner" role="alert">
          <div className="offline-text">
            <strong>{verworfen.length} offline gespeicherte Eingabe{verworfen.length !== 1 ? 'n' : ''} vom Server abgelehnt, NICHT gespeichert:</strong>
            {verworfen.slice(-5).map((v, i) => (
              <span key={i}>{v.label} ({new Date(v.ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}): {v.grund}</span>
            ))}
          </div>
          <button className="offline-close-btn" onClick={() => setVerworfen([])} title="Gelesen">✕</button>
        </div>
      )}

      {/* Online, aber es wartet noch etwas (letzter Versuch scheiterte) */}
      {!isOffline && pendingCount > 0 && syncState === null && (
        <div className="offline-banner sync-partial-banner">
          <span>{pendingCount} Ergebnis{pendingCount !== 1 ? 'se' : ''} warte{pendingCount !== 1 ? 'n' : 't'} auf Verbindung</span>
        </div>
      )}

      {/* Offline-Banner mit Queue-Count */}
      {isOffline && (
        <div className="offline-banner offline-active">
          <span className="offline-icon">📵</span>
          <div className="offline-text">
            <strong>Kein Internet</strong>
            {pendingCount > 0
              ? <span>{pendingCount} Ergebnis{pendingCount !== 1 ? 'se' : ''} warte{pendingCount !== 1 ? 'n' : 't'} auf Verbindung</span>
              : <span>Nur Wertungen und Ergebnisse werden zwischengespeichert</span>
            }
          </div>
          {pendingCount > 0 && (
            <span className="offline-queue-badge">{pendingCount}</span>
          )}
        </div>
      )}

      {/* Wieder-Online (nur wenn nichts sync'd werden musste) */}
      {showReconnected && !isOffline && syncState === null && (
        <div className="offline-banner reconnected-banner">
          <span>✅ Wieder online</span>
        </div>
      )}

    </div>
  );
}
